import { HoldTypeChipsCell } from '@planning/components/hold/DisplayHoldsButton'
import {
  ContainerJourneyStatusChip,
  ContainerJourneyStatusChipWrapper,
} from '@planning/components/molecules/ContainerJourneyStatusChip'
import { TFnType } from '@tolgee/react'
import { TomTableEmptyCell, TomTableUnassignedCell, YesNoBadge } from '@tom-ui/ui'
import { ContainerNumber, formatPosition } from '@tom-ui/utils'
import { MRT_ColumnDef } from 'material-react-table'
import { ReleaseReferenceTableColumn } from '../Components/atoms/ReleaseReferenceTableColumn'
import { LoadListTableColumnsDto } from '../Stores/VesselVisitOrderListViewStore'
import { columnFilterPresets } from './columnFilterConfigs'

export const getLoadListColumns = (t: TFnType): MRT_ColumnDef<LoadListTableColumnsDto>[] => [
  {
    accessorKey: 'number',
    header: t('containerNo', 'Container No.'),
    size: 175,
    Cell: ({ renderedCellValue }) =>
      renderedCellValue ? (
        <ContainerNumber value={String(renderedCellValue)} format={false} />
      ) : (
        <TomTableUnassignedCell renderedCellValue={renderedCellValue} />
      ),
    ...columnFilterPresets.searchMandatory(),
  },
  {
    accessorKey: 'status',
    header: t('status', 'Status'),
    size: 175,
    ...columnFilterPresets.orderStatusMultiSelect(),
    Cell: cell => {
      const item = cell.row.original
      const order = item.originalOrder

      if (!order.containerJourney) return <ContainerJourneyStatusChip status={item.status ?? '-'} />

      return <ContainerJourneyStatusChipWrapper containerJourney={order.containerJourney} />
    },
  },
  {
    accessorKey: 'approved',
    header: t('approved', 'Approved'),
    size: 80,
    ...columnFilterPresets.yesNoSelect(),
    Cell: cell => <YesNoBadge isYes={cell.row.original.approved?.toLowerCase() === 'yes'} />,
  },
  {
    accessorKey: 'hold',
    header: t('holdTypes', 'Hold Types'),
    size: 300,
    ...columnFilterPresets.arrayContainsValue(),
    Cell: cell => <HoldTypeChipsCell containerJourneyId={cell.row.original.containerJourneyId} />,
  },
  {
    accessorKey: 'isoCode',
    header: t('isoCode', 'Iso code'),
    size: 100,
    Cell: TomTableEmptyCell,
    ...columnFilterPresets.searchMandatory(),
  },
  {
    accessorKey: 'archetype',
    header: t('archetype', 'Archetype'),
    size: 100,
    Cell: TomTableEmptyCell,
    ...columnFilterPresets.searchMandatory(),
  },
  {
    accessorKey: 'category',
    header: t('category', 'Category'),
    size: 175,
    ...columnFilterPresets.exportTransshipmentOverlanded(),
  },
  {
    accessorKey: 'shippingLine',
    header: t('shippingLine', 'Shipping Line'),
    size: 175,
    ...columnFilterPresets.searchMandatory(),
  },
  {
    accessorKey: 'shipper',
    header: t('shipper', 'Shipper'),
    size: 175,
    Cell: TomTableEmptyCell,
    ...columnFilterPresets.searchOptional(),
  },
  {
    accessorKey: 'forwarder',
    header: t('forwarder', 'Forwarder'),
    size: 175,
    Cell: TomTableEmptyCell,
    ...columnFilterPresets.searchOptional(),
  },
  {
    accessorKey: 'cargoStatus',
    header: t('cargoStatus', 'Cargo Status'),
    size: 170,
    ...columnFilterPresets.fullEmptySelect(),
  },
  {
    accessorKey: 'pod',
    header: t('pod', 'POD'),
    size: 80,
    Cell: TomTableEmptyCell,
    ...columnFilterPresets.searchOptional(),
  },
  {
    accessorKey: 'fnd',
    header: t('fnd', 'FND'),
    size: 80,
    Cell: TomTableEmptyCell,
    ...columnFilterPresets.searchOptional(),
  },
  {
    accessorKey: 'grossWeight',
    header: t('grossWeight', 'Gross Weight'),
    size: 175,
    ...columnFilterPresets.numericMandatory(),
  },
  {
    accessorKey: 'vgm',
    header: t('vgm', 'VGM'),
    size: 175,
    ...columnFilterPresets.numericMandatory(),
  },
  {
    accessorKey: 'weightClass',
    header: t('weightClass', 'Weight Class'),
    size: 175,
    Cell: TomTableEmptyCell,
    ...columnFilterPresets.searchOptional(),
  },
  {
    accessorKey: 'bookingNo',
    header: t('bookingNo', 'Booking No.'),
    size: 175,
    Cell: TomTableEmptyCell,
    ...columnFilterPresets.searchOptional(),
  },
  {
    accessorKey: 'soNumber',
    header: t('soNumber', 'S/O Number'),
    size: 175,
    Cell: TomTableEmptyCell,
    ...columnFilterPresets.searchOptional(),
  },
  {
    accessorKey: 'numericReleaseReference',
    accessorFn: row => row.numericReleaseReference ?? row.nonNumericReleaseReference,
    header: t('releaseRef', 'Release ref.'),
    size: 175,
    Cell: cell => (
      <ReleaseReferenceTableColumn
        numericReleaseReference={cell.row.original.numericReleaseReference}
        nonNumericReleaseReference={cell.row.original.nonNumericReleaseReference}
        renderedCellValue={cell.renderedCellValue}
      />
    ),
    ...columnFilterPresets.searchOptional(),
  },
  {
    accessorKey: 'imoClass',
    header: t('imoClass', 'IMO Class'),
    size: 175,
    ...columnFilterPresets.arrayContainsValue(),
  },
  {
    accessorKey: 'oog',
    header: t('oog', 'OOG'),
    size: 80,
    ...columnFilterPresets.yesNoSelect(),
  },
  {
    accessorKey: 'cooling',
    header: t('cooling', 'Cooling'),
    size: 80,
    ...columnFilterPresets.yesNoSelect(),
  },
  {
    accessorKey: 'plannedPos',
    header: t('plannedPos', 'Planned pos.'),
    size: 180,
    Cell: TomTableEmptyCell,
    accessorFn: row =>
      row.destinationPosition ? formatPosition(row.destinationPosition) : undefined,
    ...columnFilterPresets.searchOptional(),
  },
  {
    accessorKey: 'outboundPos',
    header: t('outboundPos', 'Outbound pos.'),
    size: 180,
    Cell: TomTableEmptyCell,
    accessorFn: row => (row.actualPosition ? formatPosition(row.actualPosition) : undefined),
    ...columnFilterPresets.searchOptional(),
  },
  {
    accessorKey: 'stowageInstructions',
    header: t('stowageInstructions', 'Stowage Instructions'),
    size: 220,
    Cell: TomTableEmptyCell,
    ...columnFilterPresets.searchOptional(),
  },
]
